import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { User } from '@lsq-frontend/remote-services/enterprise/common';
import {
    SendPhoneNumberVerificationCodeRequest,
    SendPhoneNumberVerificationCodeService,
    ValidatePhoneNumberVerificationCodeService,
    ValidateVerificationCodeRequest
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    AuthenticationService,
    ignoreNullish,
    LsqValidators,
    Modal,
    ModalWorkflowService
} from '@lsq-frontend/common-ui';
import { BehaviorSubject } from 'rxjs';
import { pluck } from 'rxjs/operators';
import { ChooseMfaModalComponent } from './choose-mfa-modal/choose-mfa-modal.component';
import { EditTotpModalComponent } from './edit-totp-modal/edit-totp-modal.component';
import { EditTwoFactorPhoneModalComponent } from './edit-two-factor-phone-modal/edit-two-factor-phone-modal.component';
import { EditTwoFactorVerifyModalComponent } from './edit-two-factor-verify-modal/edit-two-factor-verify-modal.component';
import { MfaConfirmationModalComponent } from './mfa-confirmation-modal.component/mfa-confirmation-modal.component';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';

@Injectable({ providedIn: 'root' })
export class TwoFactorService {
    public userPhoneNumberToVerifyForm = new FormGroup({
        phoneNumber: new FormControl('', [Validators.required, LsqValidators.phone])
    });
    public userPhoneNumber$ = this._userService.user$.pipe(
        ignoreNullish(),
        pluck('userPhoneNumber')
    );

    public user: User | null;

    public preferredMfaMethod: string;

    public preferredMfaMethodSubject = new BehaviorSubject<string>('');

    constructor(
        private _sendPhoneNumberVerificationCodeService: SendPhoneNumberVerificationCodeService,
        private _validatePhoneNumberVerificationCodeService: ValidatePhoneNumberVerificationCodeService,
        private _userService: UserService,
        private _modalWorkflowService: ModalWorkflowService,
        public authenticationService: AuthenticationService
    ) {
        SingletonServiceGuard.register('TwoFactorService');
        this._userService.user$.subscribe((val) => (this.user = val));
    }

    public async getPreferredMethod() {
        const preferredMFA = await this.authenticationService.getUserPreferredMfa();
        this.preferredMfaMethod = preferredMFA;
        this.preferredMfaMethodSubject.next(this.preferredMfaMethod);
    }

    public async sendIdentityVerificationToPhone(): Promise<void> {
        await this._sendPhoneNumberVerificationCodeService.executeOnce(
            new SendPhoneNumberVerificationCodeRequest({
                phoneNumber: this.userPhoneNumberToVerifyForm.get('phoneNumber').value
            })
        );
    }

    // TODO(story/26848): Figure out why this is resulting in a navigate-to-home.
    public async verifyIdentity(verificationCode: string): Promise<void> {
        await this._validatePhoneNumberVerificationCodeService.executeOnce(
            new ValidateVerificationCodeRequest({ verificationCode })
        );
        await this._userService.getAndEmitCurrentUser();
    }

    public editTwoFactorPhoneNumber(): void {
        this.userPhoneNumberToVerifyForm.get('phoneNumber').reset('');
        this._modalWorkflowService.showWorkflow([
            new Modal({
                component: EditTwoFactorPhoneModalComponent
            }),
            new Modal({
                component: EditTwoFactorVerifyModalComponent
            }),
            new Modal({
                component: MfaConfirmationModalComponent
            })
        ]);
    }

    public enrollTotpMfa(): void {
        this._modalWorkflowService.showWorkflow([
            new Modal({
                component: EditTotpModalComponent
            }),
            new Modal({
                component: MfaConfirmationModalComponent
            })
        ]);
    }

    // If user is enrolled with authenticator, don't allow them to go back.
    // If enrolled with SMS, allow them to choose SMS or authenticator.
    public enrollMFA(mfaType: string): void {
        if (mfaType == 'SOFTWARE_TOKEN_MFA') {
            this.enrollTotpMfa();
        } else {
            this._modalWorkflowService.showWorkflow([
                new Modal({
                    component: ChooseMfaModalComponent
                })
            ]);
        }
    }
}
